import { memoize } from 'lodash';

import { labware as labwareV1_0 } from './labware';
import { labware as labwareV1_1 } from './labware-v1.1';
import type { LabwareDef, LabwareDefByDefURI } from './types';
import type { LabwareType } from '../types';

export type LabwareVersion = '1.0' | '1.1';

const getLabwareByVersion = (version: LabwareVersion): LabwareDef[] => {
  switch (version) {
    case '1.1':
      return labwareV1_1;
    case '1.0':
      return labwareV1_0;
    default:
      version satisfies never;
      throw new Error(`Unsupported labware version: ${version}`);
  }
};

export const getLabwareURI = memoize((def: LabwareDef): string => {
  return `${def.namespace}/${def.loadName}`;
});

export const getAllLabwareDefinitions: (
  version?: LabwareVersion
) => LabwareDefByDefURI = memoize(
  (version: LabwareVersion = '1.0'): LabwareDefByDefURI => {
    const labwareData = getLabwareByVersion(version);
    return labwareData.reduce<LabwareDefByDefURI>(
      (acc, def) => ({
        ...acc,
        [getLabwareURI(def)]: def,
      }),
      {}
    );
  }
);

export const getAllLabwareDefinitionsOfType: (
  labwareType: LabwareType,
  version?: LabwareVersion
) => LabwareDef[] = memoize(
  (labwareType: LabwareType, version: LabwareVersion = '1.0'): LabwareDef[] => {
    const labwareData = getLabwareByVersion(version);
    const result = Object.values(labwareData).filter(
      (def) => def.type === labwareType
    );
    return result.sort((a, b) => a.displayName.localeCompare(b.displayName));
  },
  // Custom resolver to create cache key from both parameters
  (labwareType: LabwareType, version: LabwareVersion = '1.0') =>
    `${labwareType}-${version}`
);
